import React from "react";

type DeleteConfirmProps = {
  open: boolean;
  /** Shown in the default message when `message` is not provided (non–project variant). */
  dynamicName: string;
  onCancel: () => void;
  onDelete: () => void | Promise<void>;
  /** Optional soft-delete action (shows a third button when provided). */
  onSoftDelete?: () => void | Promise<void>;
  /** Modal heading (defaults: "Confirm Delete", or "Delete Project" when `showTrashIcon` is true). */
  title?: string;
  /** Full message body; when set, replaces the default paragraph that uses `dynamicName`. */
  message?: React.ReactNode;
  /** Primary button label (default: "Delete", or "Delete Project" when `showTrashIcon` is true). */
  confirmLabel?: string;
  /** Soft-delete button label (default: "Soft Delete"). */
  softDeleteLabel?: string;
  /** Cancel/close button label (default: "Cancel"). */
  cancelLabel?: string;
  /** Red trash icon + two-column header (project delete UI). */
  showTrashIcon?: boolean;
  /** Disables buttons (e.g. while API request is in flight). */
  isDeleting?: boolean;
};

const DeleteConfirmModal = ({
  open,
  dynamicName,
  onCancel,
  onDelete,
  onSoftDelete,
  title,
  message,
  confirmLabel,
  softDeleteLabel = "Soft Delete",
  cancelLabel = "Cancel",
  showTrashIcon,
  isDeleting,
}: DeleteConfirmProps) => {
  if (!open) return null;

  const heading =
    title ?? (showTrashIcon ? "Delete Project" : "Confirm Delete");

  const defaultBody = (
    <>
      Are you sure you want to delete this <b>{dynamicName}</b>? This action
      cannot be undone.
    </>
  );

  const projectDefaultMessage =
    "Are you sure you want to delete this project? This action cannot be undone and all associated data will be permanently removed.";

  const body =
    message !== undefined
      ? message
      : showTrashIcon
        ? projectDefaultMessage
        : defaultBody;

  const primaryLabel =
    confirmLabel ?? (showTrashIcon ? "Delete Project" : "Delete");

  const busy = Boolean(isDeleting);
  const hasSoftDelete = typeof onSoftDelete === "function";

  return (
    <div className="dc-overlay">
      <div
        className={`delete-modal${showTrashIcon ? " delete-modal--project" : ""}${
          hasSoftDelete ? " delete-modal--triple" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
      >
        {showTrashIcon ? (
          <div className="delete-modal__row">
            <div className="delete-modal__icon-circle" aria-hidden>
              <i className="ri-delete-bin-line" />
            </div>
            <div className="delete-modal__col">
              <h4 id="delete-confirm-title" className="delete-title">
                {heading}
              </h4>
              <p className="delete-text delete-text--project">{body}</p>
            </div>
          </div>
        ) : (
          <>
            <h4 id="delete-confirm-title" className="delete-title">
              {heading}
            </h4>
            <p className="delete-text">{body}</p>
          </>
        )}

        <div className={`delete-actions${hasSoftDelete ? " delete-actions--triple" : ""}`}>
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          {hasSoftDelete && (
            <button
              type="button"
              className="soft-delete-btn"
              onClick={() => void onSoftDelete()}
              disabled={busy}
            >
              {busy ? "…" : softDeleteLabel}
            </button>
          )}
          <button
            type="button"
            className="delete-btn"
            onClick={() => void onDelete()}
            disabled={busy}
          >
            {busy ? "…" : primaryLabel}
          </button>
        </div>
      </div>

      <style>
        {`
        .dc-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .delete-modal {
          width: 485px;
          max-width: calc(100vw - 32px);
          background: #fff;
          border-radius: 14px;
          padding: 28px 32px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          animation: dcFadeIn 0.15s ease-in-out;
        }

        .delete-modal--project {
          width: 520px;
        }

        .delete-modal--triple {
          width: 560px;
        }

        .delete-modal__row {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 8px;
        }

        .delete-modal__icon-circle {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          border-radius: 999px;
          border: 2px solid #e7000b;
          color: #e7000b;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          background: #fff;
        }

        .delete-modal__col {
          min-width: 0;
          flex: 1;
        }

        .delete-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 10px 0;
          color: #0a0a0a;
        }

        .delete-text {
          font-size: 15px;
          color: #4b5563;
          margin: 0 0 26px 0;
          line-height: 1.5;
        }

        .delete-text--project {
          margin-top: 0;
        }

        .delete-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .delete-actions--triple {
          flex-wrap: wrap;
        }

        .cancel-btn {
          padding: 8px 20px;
          background: #ffffff;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 15px;
          cursor: pointer;
          color: #0a0a0a;
          transition: background 0.2s ease;
        }
        .cancel-btn:hover:not(:disabled) {
          background: #e9ebef;
        }
        .cancel-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .soft-delete-btn {
          padding: 8px 18px;
          background: #fff7ed;
          border: 1px solid #fdba74;
          border-radius: 8px;
          font-size: 15px;
          cursor: pointer;
          color: #c2410c;
          font-weight: 500;
          transition: background 0.2s ease;
        }
        .soft-delete-btn:hover:not(:disabled) {
          background: #ffedd5;
        }
        .soft-delete-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        .delete-btn {
          padding: 8px 22px;
          background: #e7000b;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          cursor: pointer;
          color: white;
          font-weight: 500;
          transition: background 0.2s ease;
        }
        .delete-btn:hover:not(:disabled) {
          background: #c10007;
        }
        .delete-btn:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        @keyframes dcFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}
      </style>
    </div>
  );
};

export default DeleteConfirmModal;
